import { useState } from 'react';

interface Order {
  id: string;
  date: string;
  status: 'delivered' | 'shipped' | 'processing';
  total: number;
  items: {
    name: string;
    quantity: number;
    price: number;
    image: string;
  }[];
}

export default function PurchaseHistory() {
  const [orders] = useState<Order[]>([
    {
      id: 'ORD-2024-001',
      date: '2024-01-15',
      status: 'delivered',
      total: 897,
      items: [
        {
          name: 'Moringa Powder - 100g',
          quantity: 2,
          price: 299,
          image: 'https://static.readdy.ai/image/9d639ef6c9792709f408f68c264ec65c/259510fa1447af2896f61d3473620b43.png'
        },
        {
          name: 'Moringa Tablets - 60 Count',
          quantity: 1,
          price: 399,
          image: 'https://static.readdy.ai/image/9d639ef6c9792709f408f68c264ec65c/ec501008434a48d659f10ea9ddd79ab9.png'
        }
      ]
    },
    {
      id: 'ORD-2024-002',
      date: '2024-01-28',
      status: 'shipped',
      total: 598,
      items: [
        {
          name: 'Moringa Powder - 100g',
          quantity: 1,
          price: 299,
          image: 'https://static.readdy.ai/image/9d639ef6c9792709f408f68c264ec65c/259510fa1447af2896f61d3473620b43.png'
        },
        {
          name: 'Moringa Tablets - 60 Count',
          quantity: 1,
          price: 399,
          image: 'https://static.readdy.ai/image/9d639ef6c9792709f408f68c264ec65c/ec501008434a48d659f10ea9ddd79ab9.png'
        }
      ]
    },
    {
      id: 'ORD-2024-003',
      date: '2024-02-05',
      status: 'processing',
      total: 897,
      items: [
        {
          name: 'Moringa Powder - 100g',
          quantity: 3,
          price: 299,
          image: 'https://static.readdy.ai/image/9d639ef6c9792709f408f68c264ec65c/259510fa1447af2896f61d3473620b43.png'
        }
      ]
    }
  ]);

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'delivered':
        return 'ri-checkbox-circle-line';
      case 'shipped':
        return 'ri-truck-line';
      case 'processing':
        return 'ri-time-line';
      default:
        return 'ri-shopping-bag-line';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900">Purchase History</h2>
        <p className="text-sm text-gray-600 mt-1">View all your past orders and track current ones</p>
      </div>

      <div className="divide-y divide-gray-200">
        {orders.map((order) => (
          <div key={order.id} className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">{order.id}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Ordered on {new Date(order.date).toLocaleDateString('en-IN', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                  <i className={`${getStatusIcon(order.status)} w-4 h-4 flex items-center justify-center`}></i>
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
                <p className="text-lg font-bold text-gray-900 mt-2">₹{order.total}</p>
              </div>
            </div>

            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{item.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">Quantity: {item.quantity}</p>
                  </div>
                  <p className="font-semibold text-gray-900">₹{item.price * item.quantity}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-3">
              <button className="flex-1 px-4 py-2 border border-emerald-600 text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap">
                View Details
              </button>
              {order.status === 'delivered' && (
                <button className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
                  Reorder
                </button>
              )}
              {order.status === 'shipped' && (
                <button className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
                  Track Order
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
